// src/components/wordbookGroup/WorkbookGroupForm.tsx
import {FC, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import {ArrowBack, Save} from '@mui/icons-material';
import {
    useCreateWordbookGroup,
    useUpdateWordbookGroup,
    useWordbookGroup
} from '../../hooks/useWordbookGroup';
import {CreateWordbookGroupRequest} from '../../models/wordbookGroup/createWordbookGroupRequest';
import {Visibility} from '../../types/wordbookGroup';
import {ImageUpload} from '../common/ImageUpload';

export const WordbookGroupForm: FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isEditMode = !!id;
    const groupId = id ? parseInt(id) : 0;

    // Edit 모드일 때만 데이터 로드 (id가 0이면 자동으로 비활성화됨)
    const { data: group, isLoading } = useWordbookGroup(groupId);

    const createGroup = useCreateWordbookGroup();
    const updateGroup = useUpdateWordbookGroup();

    const [formData, setFormData] = useState<CreateWordbookGroupRequest>({
        title: '',
        headLang: 'en',
        meaningLang: 'ko',
        visibility: Visibility.VISIBLE,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    // Edit 모드: 기존 데이터로 초기화
    useEffect(() => {
        if (group) {
            setFormData({
                title: group.title,
                headLang: group.headLang,
                meaningLang: group.meaningLang,
                visibility: group.visibility,
                thumbnailUrl: group.thumbnailUrl,
            });
        }
    }, [group]);

    const handleChange = <K extends keyof CreateWordbookGroupRequest>(field: K, value: CreateWordbookGroupRequest[K]) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.title?.trim()) {
            newErrors.title = '제목을 입력해주세요.';
        }

        // Create 모드에서만 필수
        if (!isEditMode) {
            if (!formData.headLang) {
                newErrors.headLang = '학습 언어를 선택해주세요.';
            }
            if (!formData.meaningLang) {
                newErrors.meaningLang = '의미 언어를 선택해주세요.';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        if (isEditMode) {
            updateGroup.mutate(
                { id: groupId, data: formData },
                {
                    onSuccess: () => {
                        navigate(`/wordbook-group/${groupId}`);
                    },
                }
            );
        } else {
            createGroup.mutate(formData, {
                onSuccess: (response) => {
                    navigate(`/wordbook-group/${response.groupId}`);
                },
            });
        }
    };

    const handleCancel = () => {
        navigate(isEditMode ? `/wordbook-group/${groupId}` : '/wordbook-group');
    };

    // Edit 모드에서 로딩 중
    if (isEditMode && isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    // Edit 모드에서 데이터 없음
    if (isEditMode && !isLoading && !group) {
        return (
            <Box p={3}>
                <Alert severity="error">그룹을 찾을 수 없습니다.</Alert>
            </Box>
        );
    }

    const mutation = isEditMode ? updateGroup : createGroup;

    return (
        <Box p={3}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={handleCancel}
                >
                    {isEditMode ? '뒤로' : '목록으로'}
                </Button>
                <Typography variant="h4">
                    {isEditMode ? '그룹 수정' : '새 그룹 만들기'}
                </Typography>
            </Box>

            {mutation.isError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {mutation.error?.message ||
                        `그룹 ${isEditMode ? '수정' : '생성'}에 실패했습니다.`}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* 기본 정보 */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                기본 정보
                            </Typography>

                            {/* 제목 */}
                            <TextField
                                fullWidth
                                label="제목"
                                value={formData.title}
                                onChange={(e) => handleChange('title', e.target.value)}
                                error={!!errors.title}
                                helperText={errors.title}
                                required={!isEditMode}
                                margin="normal"
                            />

                            {/* 언어 설정 */}
                            <Grid container spacing={2} mt={1}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <FormControl
                                        fullWidth
                                        error={!!errors.headLang}
                                        required={!isEditMode}
                                    >
                                        <InputLabel>학습 언어</InputLabel>
                                        <Select
                                            value={formData.headLang || ''}
                                            onChange={(e) => handleChange('headLang', e.target.value)}
                                            label="학습 언어"
                                        >
                                            <MenuItem value="ko">한국어</MenuItem>
                                            <MenuItem value="en">영어</MenuItem>
                                            <MenuItem value="ja">일본어</MenuItem>
                                            <MenuItem value="zh">중국어</MenuItem>
                                            <MenuItem value="es">스페인어</MenuItem>
                                            <MenuItem value="fr">프랑스어</MenuItem>
                                            <MenuItem value="de">독일어</MenuItem>
                                            <MenuItem value="vi">베트남어</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <FormControl
                                        fullWidth
                                        error={!!errors.meaningLang}
                                        required={!isEditMode}
                                    >
                                        <InputLabel>의미 언어</InputLabel>
                                        <Select
                                            value={formData.meaningLang || ''}
                                            onChange={(e) => handleChange('meaningLang', e.target.value)}
                                            label="의미 언어"
                                        >
                                            <MenuItem value="ko">한국어</MenuItem>
                                            <MenuItem value="en">영어</MenuItem>
                                            <MenuItem value="ja">일본어</MenuItem>
                                            <MenuItem value="zh">중국어</MenuItem>
                                            <MenuItem value="es">스페인어</MenuItem>
                                            <MenuItem value="fr">프랑스어</MenuItem>
                                            <MenuItem value="de">독일어</MenuItem>
                                            <MenuItem value="vi">베트남어</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {/* 노출 설정 */}
                            <FormControl fullWidth margin="normal">
                                <InputLabel>노출 상태</InputLabel>
                                <Select
                                    value={formData.visibility}
                                    onChange={(e) => handleChange('visibility', e.target.value as Visibility)}
                                    label="노출 상태"
                                >
                                    <MenuItem value={Visibility.VISIBLE}>노출</MenuItem>
                                    <MenuItem value={Visibility.HIDDEN}>숨김</MenuItem>
                                </Select>
                            </FormControl>

                            {/* 소유자 ID (Create 모드에서만) */}
                            {!isEditMode && (
                                <TextField
                                    fullWidth
                                    label="소유자 ID (선택사항)"
                                    type="number"
                                    value={formData.ownerId || ''}
                                    onChange={(e) => handleChange('ownerId', e.target.value ? parseInt(e.target.value) : undefined)}
                                    helperText="비워두면 시스템 그룹으로 생성됩니다."
                                    margin="normal"
                                />
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* 썸네일 업로드 */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <ImageUpload
                                value={formData.thumbnailUrl}
                                onChange={(url) => handleChange('thumbnailUrl', url)}
                                folder="wordbook-group/thumbnails"
                                label="썸네일 이미지"
                                helperText="권장 크기: 800x600px, 최대 5MB"
                            />
                        </CardContent>
                    </Card>
                </Grid>

                {/* 버튼 */}
                <Grid size={{ xs: 12 }}>
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                            variant="outlined"
                            onClick={handleCancel}
                            disabled={mutation.isPending}
                        >
                            취소
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Save />}
                            onClick={handleSubmit}
                            disabled={mutation.isPending}
                        >
                            {mutation.isPending
                                ? `${isEditMode ? '저장' : '생성'} 중...`
                                : isEditMode ? '저장' : '생성'}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};
